import type { PastDueItem } from "@nmt/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { errorMessage, get, post } from "../api";
import { useSession } from "../session";
import { SendToCustomer } from "./email-dialog";
import { Alert, Badge, Button, Empty, Loading, Panel, PanelHeader, cx, formatCents, formatDate } from "./ui";

/** The dashboard's Past due card: invoices past their due date with a balance, oldest first. */
export function PastDueCard() {
  const { can, me } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["past-due"], queryFn: () => get<PastDueItem[]>("/api/invoices/past-due"), enabled: can("money.view") });
  const [mine, setMine] = useState<boolean | null>(null);
  const [reminding, setReminding] = useState<PastDueItem | null>(null);
  if (!can("money.view")) return null;
  const rows = q.data ?? [];
  const myId = me?.user.id ?? "";
  const haveMine = rows.some((r) => r.repIds.includes(myId));
  // Reps see theirs first (when they have any); anyone can switch to everyone's.
  const onlyMine = mine ?? haveMine;
  const shown = onlyMine ? rows.filter((r) => r.repIds.includes(myId)) : rows;
  const total = shown.reduce((n, r) => n + r.balanceCents, 0);
  return (
    <Panel id="past-due">
      <PanelHeader
        title="Past due"
        description={shown.length ? `${shown.length} invoice${shown.length === 1 ? "" : "s"}, ${formatCents(total)}` : undefined}
        action={
          <div className="flex items-center gap-2">
            {haveMine && (
              <div className="inline-flex rounded-[var(--radius-control)] bg-mist p-0.5 text-sm">
                {[true, false].map((v) => (
                  <button key={String(v)} onClick={() => setMine(v)} className={cx("rounded-[6px] px-2.5 py-1 font-semibold", onlyMine === v ? "bg-white shadow-sm" : "text-muted hover:text-ink")}>
                    {v ? "Mine" : "Everyone"}
                  </button>
                ))}
              </div>
            )}
            {can("reports.aging") && (
              <Link to="/reports" className="text-sm font-semibold text-brand hover:underline">
                Aging
              </Link>
            )}
          </div>
        }
      />
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <Alert>{errorMessage(q.error)}</Alert>
      ) : !shown.length ? (
        <Empty>Nothing past due.</Empty>
      ) : (
        <ul className="max-h-[28rem] overflow-y-auto">
          {shown.map((r) => (
            <li key={r.invoiceId} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-mist px-5 py-3 text-sm last:border-0">
              <div className="min-w-0 flex-1">
                <Link to={`/jobs/${r.jobId}?tab=invoices`} className="font-semibold hover:text-brand">
                  {r.customerName}
                </Link>
                <span className="text-muted">
                  {" "}
                  · Job #{r.jobNumber}
                  {r.jobTitle ? ` ${r.jobTitle}` : ""} · Invoice {r.number}
                </span>
                <div className="text-xs text-muted">
                  Due {formatDate(r.dueDate)}
                  {r.repName ? ` · ${r.repName}` : ""}
                  {r.remindedAt ? ` · Reminded ${formatDate(r.remindedAt)} by ${r.remindedVia}` : ""}
                </div>
              </div>
              {r.pending ? <Badge tone="wait">Payment processing</Badge> : <Badge tone={r.daysLate > 30 ? "brand" : "wait"}>{r.daysLate} days late</Badge>}
              <span className="w-24 text-right font-semibold">{formatCents(r.balanceCents)}</span>
              {r.collectable && can("invoices.edit") && (
                <Button className="px-3 py-1" onClick={() => setReminding(r)}>
                  Send reminder
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {reminding && (
        <SendToCustomer
          kind="invoice"
          id={reminding.invoiceId}
          jobId={reminding.jobId}
          purpose="overdue"
          title="Send a reminder"
          note={`A friendly past-due reminder for invoice ${reminding.number} (${formatCents(reminding.balanceCents)}), with the same pay link they were sent. You'll see the message before it goes.`}
          send={async () => (await post<{ payUrl: string }>(`/api/invoices/${reminding.invoiceId}/pay-link`)).payUrl}
          onClose={() => (setReminding(null), void qc.invalidateQueries({ queryKey: ["past-due"] }))}
        />
      )}
    </Panel>
  );
}
